import { useGlobalState, useResponsive } from '@/hooks';
import { ResponsiveCollection } from '@/interfaces';
import AffiliateSection from '@/modules/pmt/components/AffiliateSection';
import { useUpdateTimeoutShipment } from '@/modules/pmt/components/ShipmentsOverview/components/ShipmentList/components/ShipmentCard/hooks';
import { PMTAdType } from '@/modules/pmt/constants';
import { useShipmentDetailsContext } from '@/modules/pmt/context';
import { useEmailSyncPopup, useShipmentInfo } from '@/modules/pmt/hooks';
import { type ICarrierByParcel, type INotiButtonStateByParcel, ShipmentSearchStatus } from '@/modules/pmt/interfaces';
import { isOwnShipment } from '@/modules/pmt/utils';
import { gtmViewParcel } from '@/utils';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type FC, type ReactElement, useEffect, useState } from 'react';
import { AnchorWrapper } from '../AnchorWrapper';
import { PMTAdsense } from '../PMTAdsense';
import { TrackingDetailsLayout } from '../TrackingDetailsLayout';
import { MerchantSection } from './components/MerchantSection';
import { OrderSection } from './components/OrderSection';
import { RatingSection } from './components/RatingSection';
import { SMEImage } from './components/SMEImage';
import { TrackingCarrierSection } from './components/TrackingCarrierSection';
import { TrackingDetailHeader } from './components/TrackingDetailHeader';
import { TrackingEventCard } from './components/TrackingEventCard';
import { TrackingEventList } from './components/TrackingEventList';
import { TrackingDetailsProvider } from './context';
import useTrackingDetails from './useTrackingDetails.hook';

// React Query client for affiliate products feature
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

export const TrackingDetails: FC = () => {
  const { isLoggedIn, firestoreUuid } = useGlobalState();
  const { isDesktopPMT } = useResponsive([ResponsiveCollection.DesktopPMT]);
  const { showDetailsView, currentShipment, currentShipmentDocId } = useShipmentDetailsContext();
  const trackingDetails = useTrackingDetails(currentShipment);
  const { shipmentLabel, shipmentLogo, shipmentMerchantName } = useShipmentInfo(currentShipment?.data);
  const { openEmailSyncPopup } = useEmailSyncPopup();

  // Auto open mobile details view if single shipment is being tracked
  // Ticket: https://parcelperform.atlassian.net/browse/PHOENIX-1163
  useUpdateTimeoutShipment(
    isDesktopPMT ? undefined : currentShipment?.data,
    !isLoggedIn ? openEmailSyncPopup : undefined,
  );

  // FOR CARRIER DROPDOWN HANDLING
  const [carrierByParcel, setCarrierByParcel] = useState<ICarrierByParcel>({});
  // FOR SHIPMENT SUBSCRIBE HANDLING
  const [notiButtonStateByParcel, setNotiButtonStateByParcel] = useState<INotiButtonStateByParcel>({});
  // FOR SHIPMENT SUBSCRIBE-ALL API HANDLING
  const [subAllApiState, setSubAllApiState] = useState(false);

  const { carrierList, sortedEventList, eventCardDetail } = trackingDetails;

  const isShow = isDesktopPMT || showDetailsView;
  const shipmentIsOpenShipment = currentShipment?.data.isOpenShipment;
  const shipmentIsMockShipment = !!currentShipment?.data.isMockCarrierShipment;
  const shipmentIsESShipment = currentShipment?.data.isEmailSyncShipment;
  const shipmentIsSMEShipment = currentShipment?.data.isSMEShipment;

  useEffect(() => {
    if (!isDesktopPMT) return;
    // Ref: https://www.pivotaltracker.com/n/projects/2537310/stories/183861712
    gtmViewParcel(); // Desktop #1
  }, [isDesktopPMT]);

  useEffect(() => {
    if (currentShipmentDocId) {
      window.scrollTo(0, 0);
    }
  }, [currentShipmentDocId]);

  if (!currentShipmentDocId || !currentShipment?.data) return null;

  const renderSMEHeaderImage = () => {
    if (!shipmentIsSMEShipment || !currentShipment.data.merchantInfo?.headerBanner) return null;
    return <SMEImage variant='header' alt='SME Header Image' src={currentShipment.data.merchantInfo.headerBanner} />;
  };

  const renderHeader = (): ReactElement => {
    return (
      <TrackingDetailHeader
        label={shipmentLabel}
        shipmentLogo={shipmentLogo}
        merchantName={shipmentMerchantName}
        isESShipment={shipmentIsESShipment}
        isSMEShipment={shipmentIsSMEShipment}
        eventHeaderState={eventCardDetail.eventHeaderState}
      />
    );
  };

  const renderAd = () => {
    if (!isShow) return null;
    const adVariant = currentShipment.data.isSMEShipment
      ? PMTAdType.PMT_INDIVIDUAL_PARCEL_VIEW_1_SME
      : PMTAdType.PMT_INDIVIDUAL_PARCEL_VIEW_1;
    return <PMTAdsense className='pmt-individual-parcel' variant={adVariant} />;
  };

  const renderTrackingEventList = () => {
    if (shipmentIsOpenShipment) return null;

    return (
      <TrackingEventList
        // HACK: unmount-mount TrackingEventList when currentShipment's firestoreDocId changes
        key={currentShipment.data.firestoreDocId}
        carrierList={carrierList}
        eventList={sortedEventList}
        parcelId={currentShipment.data.parcel?.parcelId}
        isMockCarrierShipment={shipmentIsMockShipment}
      />
    );
  };

  const renderRatingSection = () => {
    return <RatingSection />;
  };

  const renderOrderSection = () => {
    return (
      <OrderSection
        shipmentData={currentShipment.data}
        isOwnShipment={isOwnShipment(currentShipment.data, firestoreUuid)}
      />
    );
  };

  const renderTrackingCarrierSection = () => {
    if (shipmentIsOpenShipment || shipmentIsMockShipment) {
      return null;
    }

    return <TrackingCarrierSection carrierList={carrierList} parcelId={currentShipment.data.parcel?.parcelId} />;
  };

  const renderMerchantSection = () => {
    if (!currentShipment.data.isSMEShipment) return null;
    if (!currentShipment.data.merchantInfo) return null;
    return <MerchantSection merchantInfo={currentShipment.data.merchantInfo} />;
  };

  const renderFooterImage = () => {
    if (!currentShipment.data.isSMEShipment || !currentShipment.data.merchantInfo?.footerBanner) return;

    return (
      <AnchorWrapper href={currentShipment.data.merchantInfo.footerHyperlink} target='_blank' rel='noopener noreferrer'>
        <SMEImage variant='footer' src={currentShipment.data.merchantInfo.footerBanner} alt='Footer Image' />
      </AnchorWrapper>
    );
  };

  const renderAffiliateSection = () => {
    // NEXUS-1854: the affiliate grid reads as a thin-affiliation signal on states that
    // carry no tracking content — hide it while pending/scraping (loading) and on the
    // not-found result (search timed out), keeping it only for matched shipments.
    const { searchStatus } = currentShipment.data;
    const hasNoTrackingContent =
      searchStatus === ShipmentSearchStatus.Pending ||
      searchStatus === ShipmentSearchStatus.ScrapingWithCarrier ||
      searchStatus === ShipmentSearchStatus.Timeout;

    if (!isShow || hasNoTrackingContent) return null;

    const shipmentId = currentShipment?.data.shipmentUuid || currentShipmentDocId;

    return <AffiliateSection shipmentId={shipmentId} />;
  };

  return (
    <QueryClientProvider client={queryClient}>
      <TrackingDetailsProvider
        value={{
          subAllApiState,
          carrierByParcel,
          setCarrierByParcel,
          setSubAllApiState,
          notiButtonStateByParcel,
          setNotiButtonStateByParcel,
        }}
      >
        <TrackingDetailsLayout
          isDesktopPMT={isDesktopPMT}
          showDetailsView={showDetailsView}
          smeHeaderImage={renderSMEHeaderImage()}
          header={renderHeader()}
          trackingEventCard={<TrackingEventCard {...eventCardDetail} />}
          ad={renderAd()}
          trackingEventList={renderTrackingEventList()}
          ratingSection={renderRatingSection()}
          orderSection={renderOrderSection()}
          merchantSection={renderMerchantSection()}
          trackingCarrierSection={renderTrackingCarrierSection()}
          affiliateSection={renderAffiliateSection()}
          footerImage={renderFooterImage()}
        />
      </TrackingDetailsProvider>
    </QueryClientProvider>
  );
};
